/* eslint-disable no-console */
import type {CancellablePromise} from '@yourcoach/async-utils';

import {delay} from '@yourcoach/async-utils';
import {createTrace} from '@yourcoach/trace';

import type {
  ApiRpcRequest,
  ApiRpcRequestParams,
  BatchRpcRequest,
  RpcRequest,
} from './types';

import {not} from '../../../utils/conditionalUtils';
import {envManager} from '../../../utils/env';
import {
  ErrorJwtExpired,
  ErrorJwtInvalid,
  parseRpcErrorFactory,
  RpcRequestError,
} from '../../../utils/errors/api';
import {
  AppError,
  CancelRequestError,
  HttpRequestError,
  NoConnectionError,
} from '../../../utils/errors/app';
import {redactObject} from '../../../utils/format/redact';
import {logger} from '../../../utils/logger';
import {DEFAULT_OPTIONS, delayPromises, REPEAT_DELAY} from './constants';
import {handleExpiredJWT} from './handleExpiredJWT';
import {handleInvalidJWT} from './handleInvalidJWT';
import {isBatchRequest} from './isBatchRequest';
import {logRequest} from './logRequest';
import {makeCancelable} from './makeCancelable';
import {makeRpcRequestObj} from './makeRpcRequestObj';
import {parseStatus} from './parseStatus';
import {prepareOptions} from './prepareOptions';
import {requestState} from './state';

const cancelMap = new Map<string, InstanceType<typeof AbortController>>();

const traceConfList = createTrace('conf-list');

const request = async <P extends {[key: string]: any} = ApiRpcRequestParams>(
  args: BatchRpcRequest | RpcRequest,
  abortController?: AbortController,
): Promise<any> => {
  const requestOptions = prepareOptions(args.options);

  let batchMethods = '';

  if (isBatchRequest(args)) {
    batchMethods = `BATCH -> ${args.batch.map(req => req.method).join(', ')}`;
  }

  const methodsString = batchMethods || (args as RpcRequest).method;

  // if token has expired
  if (
    (requestState.accessToken &&
      Date.now() >= requestState.accessTokenExpires) ||
    not(Boolean(requestState.accessToken))
  ) {
    apiRequest.setAccessToken({
      expires: 0,
      token: null,
    });

    if (requestOptions.withAuth) {
      if (requestState._hasConnection) {
        delayPromises[methodsString] = delay(REPEAT_DELAY);
        await delayPromises[methodsString];

        return apiRequest(args);
      } else {
        const err = new NoConnectionError();

        if (process.env.NODE_ENV === 'development') {
          const message = `with error "${err.message}".`;

          console.warn(
            `Request to ${requestState._apiEndpoint} (${methodsString}) had been failed ${message}`,
          );
        }

        throw err;
      }
    }
  }

  let payload: ApiRpcRequest | ApiRpcRequest[];

  const fetchOptions: RequestInit = {
    credentials: !envManager.get('isB2b') ? 'include' : undefined,
    method: 'POST',
    signal: abortController ? abortController.signal : undefined,
  };

  if (isBatchRequest(args)) {
    payload = args.batch.map(batchItem =>
      makeRpcRequestObj<P>({
        method: batchItem.method,
        options: prepareOptions({
          ...DEFAULT_OPTIONS,
          ...batchItem.options,
          withAuth: requestOptions.withAuth,
          withVersion: requestOptions.withVersion,
        }),
        params: (batchItem as RpcRequest<P>).params,
      }),
    );
  } else {
    payload = makeRpcRequestObj<P>({
      method: (args as RpcRequest<P>).method,
      options: requestOptions,
      params: (args as RpcRequest<P>).params,
    });
  }

  fetchOptions.body = JSON.stringify(payload);

  if (_shouldPerformRequest) {
    const shouldPerformResult = _shouldPerformRequest(payload);

    const shouldPerform =
      typeof shouldPerformResult === 'boolean'
        ? shouldPerformResult
        : shouldPerformResult.shouldPerform;

    if (!shouldPerform) {
      const error = new CancelRequestError();

      if (typeof shouldPerformResult !== 'boolean') {
        error.message = shouldPerformResult.reason;
      }

      throw error;
    }
  }

  if (_onRequestDidStart) {
    _onRequestDidStart(payload);
  }

  if (requestOptions.cancelId) {
    const prevAbortController = cancelMap.get(requestOptions.cancelId);
    const abortedPrev = Boolean(
      prevAbortController && !prevAbortController.signal.aborted,
    );

    if (abortedPrev) {
      prevAbortController?.abort();
    } else if (abortController) {
      cancelMap.set(requestOptions.cancelId, abortController);
    }

    traceConfList('api.cancel', {
      abortedPrev,
      cancelId: requestOptions.cancelId,
      registered: !abortedPrev && Boolean(abortController),
    });
  }

  let response: Response;
  let requestTime = 0;

  try {
    if (requestState._simulateBadNetwork) {
      await delay((Math.random() * (4000 - 10 + 1)) << 0);
    }

    let requestStartTime = 0;
    let requestEndTime = 0;

    if (process.env.NODE_ENV === 'development') {
      requestStartTime = performance.now();
    }

    response = await fetch(requestState._apiEndpoint, fetchOptions);

    if (process.env.NODE_ENV === 'development') {
      requestEndTime = performance.now();
    }

    requestTime = requestEndTime - requestStartTime;

    await parseStatus(response);
  } catch (responseOrError) {
    const isError = responseOrError instanceof Error;

    {
      // TODO: is it just for log message???
      let message = 'with unknown error';

      // that means response is an error object
      if (isError) {
        message = `with error "${responseOrError.message}".`;
      } else if (responseOrError.status) {
        message = `with ${responseOrError.status} HTTP status. See details below...`;
      }

      if (process.env.NODE_ENV === 'development') {
        console.warn(
          `Request to ${requestState._apiEndpoint} (${methodsString}) had been failed ${message}`,
        );
      }
    }

    let error = new AppError(responseOrError.message ?? 'Unknown error');

    if (CancelRequestError.isAbortSignalError(responseOrError)) {
      error = new CancelRequestError();
    }

    if (
      [
        'Failed to fetch',
        'Load failed', // CORS
        'NetworkError when attempting to fetch resource.',
        'Network request failed',
        'No network connection',
      ].includes(responseOrError.message)
    ) {
      error = new NoConnectionError();
    }

    if (!isError) {
      // TODO: Is this case possible???
      // has no network connection
      if (!responseOrError.status) {
        error = new NoConnectionError();
      } else {
        // @ts-expect-error: TODO: describe why
        const status = response.status;

        // @ts-expect-error: TODO: describe why
        let responseText = await response.text();

        responseText = responseText.replace(`${status}: `, '');

        error = new HttpRequestError(status, responseText);

        logRequest({
          isFailed: true,
          json: error,
          method: methodsString,
          options: fetchOptions,
          requestTime,
          // @ts-expect-error: TODO: describe why
          response: response,
        });

        if (isBatchRequest(args)) {
          throw (payload as ApiRpcRequest[])
            .filter((req: ApiRpcRequest) => req.id)
            .map(() => error);
        }
      }
    }

    throw error;
  } finally {
    if (requestOptions.cancelId) {
      cancelMap.delete(requestOptions.cancelId);
    }

    if (_onRequestDidEnd) {
      _onRequestDidEnd(payload);
    }
  }

  const json = (await response.json()) as any;

  if (isBatchRequest(args)) {
    let hasError = false;

    const results = json.map((resp, i: number) => {
      if (resp.error) {
        hasError = true;

        return parseRpcErrorFactory(resp.error, payload[i].params);
      }

      return resp.result;
    });

    if (hasError) {
      logRequest({
        isFailed: true,
        json,
        method: batchMethods,
        options: fetchOptions,
        requestTime,
        response,
      });

      results
        .filter(item => item instanceof RpcRequestError)
        .forEach(error => {
          if (_onRpcRequestError) {
            error.isBatchError = true;
            _onRpcRequestError(error);
          }
        });

      if (results.find(item => item instanceof ErrorJwtInvalid)) {
        handleInvalidJWT(delayPromises, apiRequest);

        throw results;
      }

      if (results.find(item => item instanceof ErrorJwtExpired)) {
        return handleExpiredJWT({
          apiRequestFn: apiRequest,
          args,
          delayPromisesMap: delayPromises,
          methodsString,
        });
      }

      return Promise.reject(results);
    }

    logRequest({
      json,
      method: batchMethods,
      options: fetchOptions,
      requestTime,
      response,
    });

    return Promise.resolve(results);
  }

  if (json.error) {
    const error = parseRpcErrorFactory(
      json.error,
      (payload as ApiRpcRequest).params,
    );

    logRequest({
      isFailed: true,
      json,
      method: (args as RpcRequest).method,
      options: fetchOptions,
      requestTime,
      response,
    });

    _onRpcRequestError?.(error);

    if (error instanceof ErrorJwtInvalid) {
      handleInvalidJWT(delayPromises, apiRequest);

      throw error;
    }

    if (error instanceof ErrorJwtExpired) {
      return handleExpiredJWT({
        apiRequestFn: apiRequest,
        args,
        delayPromisesMap: delayPromises,
        methodsString,
      });
    }

    throw error;
  }

  logRequest({
    json,
    method: (args as RpcRequest).method,
    options: fetchOptions,
    requestTime,
    response,
  });

  return json.result;
};

const apiRequest = <P extends {[key: string]: any} = ApiRpcRequestParams>(
  args: BatchRpcRequest<P> | RpcRequest<P>,
): CancellablePromise<any> => {
  if (not(requestState._enabled)) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('apiRequest is disabled');
    }

    return makeCancelable(
      new Promise<void>(resolve => {
        setTimeout(
          () => {
            resolve();
          },
          1000 * 60 * 60,
        );
      }).then(() => apiRequest<P>(args)),
      typeof AbortController !== 'undefined'
        ? new AbortController()
        : undefined,
    );
  }

  const abortController =
    typeof AbortController !== 'undefined' ? new AbortController() : undefined;

  return makeCancelable(request<P>(args, abortController), abortController);
};

let _onRequestDidStart:
  | ((payload: ApiRpcRequest | ApiRpcRequest[]) => void)
  | undefined;
let _onRequestDidEnd:
  | ((payload: ApiRpcRequest | ApiRpcRequest[]) => void)
  | undefined;
let _shouldPerformRequest:
  | ((
      payload: ApiRpcRequest | ApiRpcRequest[],
    ) => boolean | {reason: string; shouldPerform: boolean})
  | undefined;
let _onRpcRequestError: ((error: any) => void) | undefined;

apiRequest.configure = ({
  apiEndpoint,
  apiVersion,
  onRequestDidEnd,
  onRequestDidStart,
  onRpcRequestError,
  shouldPerformRequest,
}: {
  apiEndpoint: string;
  apiVersion: string;
  onRequestDidEnd?: (payload: ApiRpcRequest | ApiRpcRequest[]) => void;
  onRequestDidStart?: (payload: ApiRpcRequest | ApiRpcRequest[]) => void;
  onRpcRequestError?: (error: RpcRequestError) => void;
  shouldPerformRequest?: (
    payload: ApiRpcRequest | ApiRpcRequest[],
  ) => boolean | {reason: string; shouldPerform: boolean};
}) => {
  requestState._apiEndpoint = apiEndpoint;
  requestState._apiVersion = apiVersion;

  _onRequestDidStart = onRequestDidStart;
  _onRequestDidEnd = onRequestDidEnd;
  _shouldPerformRequest = shouldPerformRequest;
  _onRpcRequestError = (error: RpcRequestError) => {
    // cleanup params

    error.sentParams = redactObject(error.sentParams, [
      'access_token',
      'session_token',
      'client_id',
      'client_secret',
      'token',
      'push_token',
      'phone',
      'email',
      'first_name',
      'last_name',
      'zip_code',
      'answer', //  additional data
    ]);

    logger.rpcError(error);

    onRpcRequestError?.(error);
  };

  return apiRequest;
};

apiRequest.setAccessToken = ({
  expires,
  token,
}: {
  expires: number;
  token: null | string;
}) => {
  requestState.accessToken = token;
  requestState.accessTokenExpires = expires;

  onAccessTokenChangeListeners.forEach(cb => cb());

  return apiRequest;
};

const onAccessTokenChangeListeners = new Set<() => void>();

Object.defineProperty(apiRequest, '__onAccessTokenChange', {
  configurable: false,
  enumerable: false,
  value: (cb: () => void) => {
    onAccessTokenChangeListeners.add(cb);
  },
  writable: false,
});

Object.defineProperty(apiRequest, '__getAccessToken', {
  configurable: false,
  enumerable: false,
  value: () => {
    const {accessToken, accessTokenExpires} = requestState;

    return {accessToken, accessTokenExpires};
  },
  writable: false,
});

apiRequest.setEnabled = (enabled: boolean) => {
  requestState._enabled = enabled;

  return apiRequest;
};

apiRequest.setHasConnection = (hasConnection: boolean) => {
  requestState._hasConnection = hasConnection;

  return apiRequest;
};

apiRequest.simulateBadNetwork = (value: boolean) => {
  requestState._simulateBadNetwork = value;

  return apiRequest;
};

apiRequest.isRpcRequestError = (error: any) => {
  return !!(
    error &&
    (Array.isArray(error) ? error : [error]).some(
      item => item instanceof RpcRequestError,
    )
  );
};

apiRequest.isCanceledError = (error: any) => {
  return !!(
    error &&
    (Array.isArray(error) ? error : [error]).some(
      item => item instanceof CancelRequestError,
    )
  );
};

apiRequest.cancel = (cancelId: string) => {
  cancelMap.get(cancelId)?.abort();
};

export {apiRequest};
